/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState, useRef } from 'react'
import { DialogActions, Typography, Divider, Box, Drawer, IconButton, useTheme } from '@mui/material'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType, ButtonSize } from '../../../../components/GenericButton/styles'
import { notification } from '../../../../components/Notifications'
import ComparisonsSection from './ComparsionSection/ComparisonsSection'
import { black, shade } from '../../../../colors'
import { chartSettingsSwitches, comparisonSchema, keyStatSwitches } from '../constants'
import { SwitchElement } from 'react-hook-form-mui'
import SlicesSection from './SlicesSections/SlicesSection'
import { formatReportPayload, loadDisplaySettings, saveDisplaySettings } from '../helper'

export interface DisplaySettings {
  comparisons: Array<{ compareBy: string | null, comparison: string | null }>
  slices: Array<{ sliceBy: string | null }>
  keyStatistics: Record<string, boolean>
  chartSettings: Record<string, boolean>
}

export interface DisplayCustomizationDrawerProps {
  isModalOpen: boolean
  onClose: () => void
  fetchReports: (params: Record<string, any>) => Promise<any>
}

export const getDefaultSettings = (): DisplaySettings => ({
  comparisons: [{ compareBy: null, comparison: null }],
  slices: [{ sliceBy: '' }],
  keyStatistics: keyStatSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
    acc[name] = false
    return acc
  }, {}),
  chartSettings: chartSettingsSwitches.reduce<Record<string, boolean>>((acc, { name }) => {
    acc[name] = false
    return acc
  }, {})
})

const getStoredSettings = (): DisplaySettings => {
  const stored = localStorage.getItem('displaySettings')
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch { }
  }
  return getDefaultSettings()
}

const DisplayCustomizationDrawer: React.FC<DisplayCustomizationDrawerProps> = ({
  isModalOpen,
  onClose,
  fetchReports
}) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const wasReset = useRef(false)

  // Use correct type for useForm
  const methods = useForm<any>({
    defaultValues: loadDisplaySettings(getDefaultSettings),
    resolver: yupResolver(comparisonSchema)
  })

  const { reset, handleSubmit, formState, control, setValue, watch } = methods
  const { isDirty } = formState

  // Restore settings from localStorage when drawer opens
  useEffect(() => {
    if (isModalOpen) {
      reset(getStoredSettings())
      wasReset.current = true
    }
  }, [isModalOpen, reset])

  // Handle form submission
  const onSubmit = async (data: DisplaySettings): Promise<void> => {
    const formattedData = formatReportPayload(data)
    setLoading(true)
    try {
      await fetchReports(formattedData)
      saveDisplaySettings(data)
      onClose()
    } catch (error) {
      notification('Error submitting data', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Watchers for toggles
  const chartSettingsWatch = watch('chartSettings')
  const keyStatsWatch = watch('keyStatistics')
  const isAnySelected = Object.values(keyStatsWatch || {}).some(Boolean)

  const handleToggleAll = (): void => {
    const newValue = !isAnySelected
    keyStatSwitches.forEach(({ name }) => {
      setValue(`keyStatistics.${name}`, newValue)
    })
  }

  // Watch for single comparison logic
  const comparisons = watch('comparisons') || []
  const isSingleComparison = comparisons?.[0]?.comparison !== null

  // Only update percentage when user changes comparisons, not on reset
  useEffect(() => {
    if (wasReset.current) {
      wasReset.current = false
      return
    }
    if (isSingleComparison) {
      setValue('chartSettings.percentage', true)
    } else {
      setValue('chartSettings.percentage', false)
    }
  }, [isSingleComparison, comparisons, setValue])

  return (
    <Drawer
      anchor="right"
      open={isModalOpen}
      onClose={onClose}
      keepMounted={true}
      PaperProps={{
        sx: {
          width: theme.spacing(120),
          padding: `${theme.spacing(0, 1.5)}`,
          mb: 5,
          overflowX: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2
        }}
      >
        <Typography variant="h6" color={black[100]} fontSize={'24px'} fontWeight={700}>
          Report Insights
        </Typography>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" flexDirection="column" height="100%">
            <Box sx={{ overflowY: 'auto', padding: theme.spacing(0, 6) }}>
              <Box display={'flex'} flexDirection={'column'} gap={3} sx={{ py: 2 }}>
                <ComparisonsSection name="comparisons" control={control} />
                <SlicesSection name='slices' control={control} />
                <Divider sx={{ background: shade[650], my: 3 }} />

                <Typography fontWeight={600} fontSize={18} color={black[100]}>Key Statistics</Typography>
                <Box display="flex" flexDirection="column" px={3} gap={2}>
                  {keyStatSwitches.map(({ label, name }) => (
                    <Controller
                      key={name}
                      name={`keyStatistics.${name}`}
                      control={control}
                      render={({ field }) => (
                        <SwitchElement {...field} label={label} />
                      )}
                    />
                  ))}
                </Box>

                <Typography fontWeight={600} color={black[100]}>Chart Settings</Typography>
                <Box sx={{ px: 3 }}>
                  {chartSettingsSwitches.map(({ label, name }) => {
                    // If this is the "percentage" switch
                    const isPercentage = name === 'percentage'
                    const forceChecked = isPercentage && isSingleComparison

                    return (
                      <Controller
                        key={name}
                        name={`chartSettings.${name}`}
                        control={control}
                        render={({ field }) => (
                          <SwitchElement
                            {...field}
                            label={label}
                            checked={forceChecked ? true : field.value}
                            disabled={forceChecked ? true : field.disabled}
                          />
                        )}
                      />
                    )
                  })}
                </Box>
              </Box>
            </Box>

            <DialogActions
              sx={{
                position: 'sticky',
                bottom: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: 'white',
                py: 4,
                px: 3,
                borderTop: `1px solid ${shade[650]}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >

                <GenericButton
                  label={isAnySelected ? 'Clear All' : 'Select All'}
                  buttonType={ButtonType.Ghost}
                  buttonSize={ButtonSize.Small}
                  onClick={handleToggleAll}
                />
                <Box display="flex" gap={2} alignItems="center">
                  <GenericButton
                    label="Cancel"
                    buttonType={ButtonType.Ghost}
                    buttonSize={ButtonSize.Small}
                    onClick={onClose}
                  />
                  <GenericButton
                    type="submit"
                    label="Go"
                    loading={loading}
                    buttonType={ButtonType.Primary}
                    buttonSize={ButtonSize.Small}
                    //  disabled={!isDirty}
                  />
                </Box>
            </DialogActions>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}

export default DisplayCustomizationDrawer
